// src/pages/ArticlePublicPage.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

import Header from '../components/Header';
import Footer from '../components/Footer';
import LoginModal from '../components/LoginModal';
import RegistrationModal from '../components/RegistrationModal';
import InfoModal from '../components/InfoModal';
import { ReactComponent as ChevronUpIcon } from '../assets/icon-chevron-up.svg';
import ArticleBody from '../components/ArticleBody';
import { articleUrl } from '../utils/articleQuery';
import SEO from '../components/SEO';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

function ArticlePublicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Alleen bij een echte 'bestaat niet' krijgt de pagina noindex. Bij een tijdelijke
  // laadfout niet: Google zou de pagina dan uit de index halen.
  const [notFound, setNotFound] = useState(false);

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [privacyData, setPrivacyData] = useState(null);
  const [contactData, setContactData] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const response = await axios.get(articleUrl(slug));

        if (response.data?.data?.length > 0) {
          setArticle(response.data.data[0]);
        } else {
          setError('Artikel niet gevonden');
          setNotFound(true);
        }
      } catch (err) {
        console.error('Fout bij het ophalen van het artikel:', err);
        setError('Dit artikel laden lukte niet. Probeer het opnieuw.');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const [privacyRes, contactRes] = await Promise.all([
          axios.get(`${STRAPI_URL}/api/privacyverklaring?populate=*`),
          axios.get(`${STRAPI_URL}/api/contact-pagina?populate=*`)
        ]);
        setPrivacyData(privacyRes.data.data);
        setContactData(contactRes.data.data);
      } catch (err) {
        console.error('Fout bij het ophalen van footer data:', err);
      }
    };

    fetchFooterData();
  }, []);

  const handleLoginClick = () => setIsLoginModalOpen(true);
  const handleRegisterClick = () => setIsRegisterModalOpen(true);

  if (loading) {
    return (
      <div className="page-container seo-page">
        <Header onLoginClick={handleLoginClick} onRegisterClick={handleRegisterClick} />
        <main><p>Artikel wordt geladen...</p></main>
        <Footer
          onLoginClick={handleLoginClick}
          onPrivacyClick={() => setIsPrivacyModalOpen(true)}
          onContactClick={() => setIsContactModalOpen(true)}
        />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="page-container seo-page">
        <SEO
          title="Artikel niet gevonden | Doorloopjes"
          description="Dit artikel bestaat niet of is verplaatst."
          noindex={notFound}
        />
        <Header onLoginClick={handleLoginClick} onRegisterClick={handleRegisterClick} />
        <main>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h1>Artikel niet gevonden</h1>
            <p>{error || 'Dit artikel bestaat niet of is verplaatst.'}</p>
            <button onClick={() => navigate('/')} className="btn-primary" style={{ marginTop: '1rem' }}>
              Terug naar home
            </button>
          </div>
        </main>
        <Footer
          onLoginClick={handleLoginClick}
          onPrivacyClick={() => setIsPrivacyModalOpen(true)}
          onContactClick={() => setIsContactModalOpen(true)}
        />
      </div>
    );
  }

  const imageUrl = article.featured_image?.url || null;

  const getDescription = () => {
    if (article.summary) return article.summary;
    // Try first rich-text slice
    if (Array.isArray(article.content)) {
      const firstText = article.content.find(s => s.__component === 'article.rich-text');
      if (firstText?.text?.[0]?.children?.[0]?.text) {
        const text = firstText.text[0].children[0].text;
        return text.length > 150 ? text.substring(0, 150) + '...' : text;
      }
    }
    if (article.body && Array.isArray(article.body) && article.body[0]?.children?.[0]?.text) {
      const text = article.body[0].children[0].text;
      return text.length > 150 ? text.substring(0, 150) + '...' : text;
    }
    return 'Lees dit artikel op Doorloopjes';
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: getDescription(),
    image: imageUrl,
    datePublished: article.publication_date || article.createdAt,
    dateModified: article.updatedAt,
    author: article.auteurs?.map(author => ({
      '@type': 'Person',
      name: author.name,
      jobTitle: author.title
    })) || [],
    publisher: {
      '@type': 'Organization',
      name: 'Doorloopjes',
      logo: {
        '@type': 'ImageObject',
        url: `${window.location.origin}/images/logo.png`
      }
    }
  };

  return (
    <div className="page-container seo-page">
      <SEO
        title={`${article.title} | Doorloopjes`}
        description={getDescription()}
        image={imageUrl}
        url={`/artikel/${slug}`}
        type="article"
        structuredData={structuredData}
      />
      <Header onLoginClick={handleLoginClick} onRegisterClick={handleRegisterClick} />

      {isLoginModalOpen && (
        <LoginModal
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={() => {
            setIsLoginModalOpen(false);
            window.location.href = '/dashboard';
          }}
          onOpenRegister={() => {
            setIsLoginModalOpen(false);
            setIsRegisterModalOpen(true);
          }}
        />
      )}

      {isRegisterModalOpen && (
        <RegistrationModal
          onClose={() => setIsRegisterModalOpen(false)}
          onRegisterSuccess={() => {
            setIsRegisterModalOpen(false);
            window.location.href = '/dashboard';
          }}
        />
      )}

      <InfoModal
        show={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
        title={privacyData?.title}
        content={privacyData?.body}
        loading={false}
        showSocial={false}
      />

      <InfoModal
        show={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title={contactData?.title}
        content={contactData?.body}
        loading={false}
        showSocial={true}
      />

      <main>
        <div className="doorloopje-detail-container article-view">
          <button className="back-button" onClick={() => navigate('/')}>
            <ChevronUpIcon className="back-icon" />
            <span>Terug</span>
          </button>

          <ArticleBody article={article} />

          <div className="example-cta-section">
            <div className="example-cta-content">
              <h3>Wil je meer artikelen bekijken?</h3>
              <p>Maak gratis een account. Dan kun je alle artikelen, doorloopjes, webinars, de assistent en de kennisbank gebruiken.</p>
              <button onClick={handleRegisterClick} className="btn-primary">
                Gratis registreren
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer
        onLoginClick={handleLoginClick}
        onPrivacyClick={() => setIsPrivacyModalOpen(true)}
        onContactClick={() => setIsContactModalOpen(true)}
      />
    </div>
  );
}

export default ArticlePublicPage;
